import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CancelIcon from '@mui/icons-material/Cancel';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FolderZipIcon from '@mui/icons-material/FolderZip';

import type { Id } from '@e-socna/data-layer';

import { EllipsisTooltip } from '../../../EllipsisTooltip';
import { RightDrawer } from '../../../RightDrawer';

import { DeleteFilesDialog } from './DeleteFilesDialog';
import { FilePreviewContent } from './FilePreviewContent';
import { MoveFilesDialog } from './MoveFilesDialog';
import { RenameFileDialog } from './RenameFileDialog';
import { downloadFile } from './utils/download-file.util';
import { getDerivedFilesInfo } from './utils/get-derived-files-info.util';
import type { FileCategory } from './types/file-category.type';
import type { FileItem } from './types/file-item.type';

interface Props {
  readonly allowedFileCategories: readonly FileCategory[];
  readonly file: FileItem | null;
  readonly files: readonly FileItem[];
  readonly filesById: ReadonlyMap<Id, FileItem>;
  readonly isDeletingFile?: boolean;
  readonly isMovingFile?: boolean;
  readonly isRenamingFile?: boolean;
  readonly selectedFileCategoryId: Id;
  readonly onClose: () => void;
  readonly onDeleteFile: (fileId: Id) => Promise<void>;
  readonly onMoveFile: (
    params: Readonly<{ fileId: Id; destinationFileCategoryId: Id }>,
  ) => Promise<void>;
  readonly onNavigate: (file: FileItem) => void;
  readonly onRenameFile: (
    params: Readonly<{ fileId: Id; newOriginalName: string }>,
  ) => Promise<void>;
}

export const FilePreviewDrawer = ({
  allowedFileCategories,
  file,
  files,
  filesById,
  isDeletingFile = false,
  isMovingFile = false,
  isRenamingFile = false,
  selectedFileCategoryId,
  onClose,
  onDeleteFile,
  onMoveFile,
  onNavigate,
  onRenameFile,
}: Readonly<Props>) => {
  // Keeps the last viewed file rendered while the drawer's close transition plays out,
  // instead of the content disappearing the instant `file` goes back to null.
  // Synced during render (React's "adjusting state when a prop changes" pattern)
  // rather than in an effect, to avoid the extra post-commit render pass.
  // Starts from the file it is mounted with: a file opened straight from its
  // URL is selected before the drawer exists.
  const [displayedFile, setDisplayedFile] = useState<FileItem | null>(file);
  const [previousFileProp, setPreviousFileProp] = useState<FileItem | null>(
    file,
  );

  if (file && file !== previousFileProp) {
    setPreviousFileProp(file);
    setDisplayedFile(file);
  }

  // RightDrawer sets disableEnforceFocus, so opening it doesn't move focus off whatever
  // triggered it (the grid row's "view" button) - left there, DataGrid's own cell navigation
  // would intercept the arrow keys before our listener below ever sees them. Depends on
  // displayedFile (not file) since that's what actually mounts the ref'd container.
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (displayedFile) {
      containerRef.current?.focus();
    }
  }, [displayedFile]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const destinationFileCategories = useMemo(
    () => allowedFileCategories.filter((c) => c._id !== selectedFileCategoryId),
    [allowedFileCategories, selectedFileCategoryId],
  );

  const derivedFilesInfo = useMemo(
    () =>
      displayedFile ? getDerivedFilesInfo(displayedFile, filesById) : null,
    [displayedFile, filesById],
  );

  const currentIndex = useMemo(
    () =>
      displayedFile ? files.findIndex((f) => f._id === displayedFile._id) : -1,
    [displayedFile, files],
  );

  const previousFile = currentIndex > 0 ? files[currentIndex - 1] : undefined;
  const nextFile =
    currentIndex >= 0 && currentIndex < files.length - 1
      ? files[currentIndex + 1]
      : undefined;

  const handlePrevious = useCallback(() => {
    if (previousFile) {
      onNavigate(previousFile);
    }
  }, [previousFile, onNavigate]);

  const handleNext = useCallback(() => {
    if (nextFile) {
      onNavigate(nextFile);
    }
  }, [nextFile, onNavigate]);

  // Skipped while a confirmation dialog is open so the arrow keys don't switch the
  // previewed file out from under that confirmation.
  useEffect(() => {
    if (!file || isDeleteDialogOpen || isMoveDialogOpen || isRenameDialogOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        handlePrevious();
        return;
      }
      if (event.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    file,
    isDeleteDialogOpen,
    isMoveDialogOpen,
    isRenameDialogOpen,
    handlePrevious,
    handleNext,
  ]);

  const handleDownload = useCallback(() => {
    if (displayedFile) {
      downloadFile(displayedFile.uri);
    }
  }, [displayedFile]);

  const openDeleteDialog = useCallback(() => {
    setDeleteError(null);
    setIsDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
  }, []);

  const openMoveDialog = useCallback(() => {
    setMoveError(null);
    setIsMoveDialogOpen(true);
  }, []);

  const closeMoveDialog = useCallback(() => {
    setIsMoveDialogOpen(false);
  }, []);

  const openRenameDialog = useCallback(() => {
    setRenameError(null);
    setIsRenameDialogOpen(true);
  }, []);

  const closeRenameDialog = useCallback(() => {
    setIsRenameDialogOpen(false);
  }, []);

  const handleConfirmMove = useCallback(
    (destinationFileCategoryId: Id) => {
      if (!displayedFile) {
        return;
      }
      onMoveFile({ fileId: displayedFile._id, destinationFileCategoryId })
        .then(() => {
          setIsMoveDialogOpen(false);
          onClose();
        })
        .catch((error: unknown) => {
          setMoveError(
            error instanceof Error ? error.message : 'Une erreur est survenue.',
          );
        });
    },
    [displayedFile, onMoveFile, onClose],
  );

  const handleConfirmRename = useCallback(
    (newOriginalName: string) => {
      if (!displayedFile) {
        return;
      }
      onRenameFile({ fileId: displayedFile._id, newOriginalName })
        .then(() => {
          setIsRenameDialogOpen(false);
          setDisplayedFile((prev) =>
            prev ? { ...prev, originalName: newOriginalName } : prev,
          );
        })
        .catch((error: unknown) => {
          setRenameError(
            error instanceof Error ? error.message : 'Une erreur est survenue.',
          );
        });
    },
    [displayedFile, onRenameFile],
  );

  const handleConfirmDelete = useCallback(() => {
    if (!displayedFile) {
      return;
    }

    onDeleteFile(displayedFile._id)
      .then(() => {
        setIsDeleteDialogOpen(false);
        onClose();
      })
      .catch((error: unknown) => {
        setDeleteError(
          error instanceof Error ? error.message : 'Une erreur est survenue.',
        );
      });
  }, [displayedFile, onDeleteFile, onClose]);

  return (
    <RightDrawer isOpen={!!file} onDrawerClose={onClose}>
      {!!displayedFile && (
        <Box
          ref={containerRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            outline: 'none',
            width: '100%',
          }}
          tabIndex={-1}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', mb: 2 }}>
            <Tooltip title="Fichier précédent">
              <span>
                <IconButton disabled={!previousFile} onClick={handlePrevious}>
                  <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Fichier suivant">
              <span>
                <IconButton disabled={!nextFile} onClick={handleNext}>
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <EllipsisTooltip title={displayedFile.originalName}>
              <Typography noWrap sx={{ flexGrow: 1, mx: 1 }} variant="h6">
                {displayedFile.originalName}
              </Typography>
            </EllipsisTooltip>
            <Tooltip
              title={
                derivedFilesInfo?.compressedFile
                  ? 'Version compressée disponible'
                  : 'Aucune version compressée'
              }
            >
              <FolderZipIcon
                color={
                  derivedFilesInfo?.compressedFile ? 'success' : 'disabled'
                }
                sx={{ mr: 1 }}
              />
            </Tooltip>
            <Tooltip
              title={
                derivedFilesInfo?.hasThumbnail
                  ? 'Vignette disponible'
                  : 'Aucune vignette'
              }
            >
              <CollectionsIcon
                color={derivedFilesInfo?.hasThumbnail ? 'success' : 'disabled'}
                sx={{ mr: 1 }}
              />
            </Tooltip>
            <Tooltip title="Télécharger">
              <IconButton onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Renommer">
              <IconButton onClick={openRenameDialog}>
                <DriveFileRenameOutlineIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Changer de catégorie">
              <span>
                <IconButton
                  disabled={destinationFileCategories.length === 0}
                  onClick={openMoveDialog}
                >
                  <DriveFileMoveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton color="error" onClick={openDeleteDialog}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fermer le volet">
              <IconButton aria-label="Fermer le volet" onClick={onClose}>
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <FilePreviewContent
            file={displayedFile}
            useCompressedVersion={!!derivedFilesInfo?.compressedFile}
          />
          <RenameFileDialog
            error={renameError}
            isLoading={isRenamingFile}
            isOpen={isRenameDialogOpen}
            onCancel={closeRenameDialog}
            onConfirm={handleConfirmRename}
            originalName={displayedFile.originalName}
          />
          <MoveFilesDialog
            categories={destinationFileCategories}
            count={1}
            error={moveError}
            isLoading={isMovingFile}
            isOpen={isMoveDialogOpen}
            onCancel={closeMoveDialog}
            onConfirm={handleConfirmMove}
          />
          <DeleteFilesDialog
            count={1}
            error={deleteError}
            isLoading={isDeletingFile}
            isOpen={isDeleteDialogOpen}
            onCancel={closeDeleteDialog}
            onConfirm={handleConfirmDelete}
          />
        </Box>
      )}
    </RightDrawer>
  );
};
